import * as THREE from 'three';
import { GUN_RANGE } from './layout';
import { box, cyl, M, mat, mesh } from './materials';

/**
 * Bubba's Gun Range, the moving parts: steel pop-up silhouettes that flip up out of
 * the dirt and fall back down, a possum that runs on a cable, clay pigeons from a
 * trap house and a rail of beer cans. The shooting stage drives all of it.
 */

export type TargetKind = 'possum' | 'coon' | 'deer' | 'gong' | 'bigfoot';

export interface PopTarget {
  kind: TargetKind;
  points: number;
  /** Base on the ground; the plate hinges at its bottom edge. */
  root: THREE.Group;
  plate: THREE.Group;
  /** What a bullet has to hit. */
  hit: THREE.Mesh;
  /** 0 = down flat, 1 = standing. Animated toward `want`. */
  up: number;
  want: number;
  /** When it drops again (stage time). */
  until: number;
}

export interface RangeCan {
  mesh: THREE.Mesh;
  home: THREE.Vector3;
  hit: boolean;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
}

export interface Range {
  targets: PopTarget[];
  /** The running possum: rides a cable across the range. */
  runner: { root: THREE.Group; hit: THREE.Mesh; fromX: number; toX: number; z: number };
  cans: RangeCan[];
  /** Where the clays come from. */
  trap: THREE.Vector3;
  clay: THREE.Mesh;
  /** Where you stand to shoot (the middle lanes of the shed), facing north. */
  lanes: THREE.Vector3[];
  /** Puts everything back up on the rail and down in the dirt. */
  reset(): void;
}

const ORANGE = '#ff7a1a';

/** A flat silhouette (in the xy plane, 3 cm thick, standing on y = 0). */
function silhouette(points: Array<[number, number]>, color: string): THREE.Mesh {
  const shape = new THREE.Shape();
  points.forEach(([x, y], i) => (i ? shape.lineTo(x, y) : shape.moveTo(x, y)));
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.03, bevelEnabled: false });
  geo.translate(0, 0, -0.015);
  return mesh(geo, mat(color, { rough: 0.5, metal: 0.4 }), 0, 0, 0);
}

const SHAPES: Record<TargetKind, { points: Array<[number, number]>; color: string; score: number }> = {
  // Nose to the left, tail curling off the right.
  possum: {
    color: ORANGE,
    score: 1,
    points: [
      [-0.42, 0.18],
      [-0.25, 0.3],
      [-0.2, 0.4],
      [-0.12, 0.33],
      [0.15, 0.36],
      [0.3, 0.28],
      [0.38, 0.12],
      [0.62, 0.3],
      [0.66, 0.26],
      [0.4, 0.04],
      [0.3, 0.0],
      [-0.25, 0.0],
      [-0.3, 0.1],
    ],
  },
  coon: {
    color: '#e8e2d0',
    score: 1,
    points: [
      [-0.36, 0.22],
      [-0.22, 0.36],
      [-0.18, 0.5],
      [-0.08, 0.4],
      [0.2, 0.42],
      [0.34, 0.3],
      [0.62, 0.42],
      [0.66, 0.34],
      [0.38, 0.12],
      [0.32, 0.0],
      [-0.22, 0.0],
      [-0.26, 0.14],
    ],
  },
  deer: {
    color: '#e8e2d0',
    score: 2,
    points: [
      [-0.55, 0.0],
      [-0.5, 0.7],
      [-0.62, 0.95],
      [-0.78, 1.2],
      [-0.72, 1.45],
      [-0.6, 1.25],
      [-0.55, 1.5],
      [-0.45, 1.3],
      [-0.48, 1.05],
      [-0.3, 0.85],
      [0.5, 0.9],
      [0.62, 0.75],
      [0.55, 0.0],
      [0.42, 0.0],
      [0.4, 0.5],
      [-0.35, 0.5],
      [-0.4, 0.0],
    ],
  },
  gong: { color: '#d9d4c7', score: 2, points: [] },
  bigfoot: {
    color: '#2a1d14',
    score: 5,
    points: [
      [-0.35, 0.0],
      [-0.3, 0.9],
      [-0.62, 1.55],
      [-0.5, 1.62],
      [-0.28, 1.25],
      [-0.25, 1.75],
      [-0.12, 1.95],
      [0.12, 1.95],
      [0.25, 1.75],
      [0.28, 1.25],
      [0.5, 1.62],
      [0.62, 1.55],
      [0.3, 0.9],
      [0.35, 0.0],
      [0.08, 0.0],
      [0.0, 0.7],
      [-0.08, 0.0],
    ],
  },
};

/** A low dirt berm in front of a target: the plate drops down behind it out of sight. */
function mound(width: number): THREE.Mesh {
  const s = new THREE.Shape();
  s.moveTo(-0.55, 0);
  s.lineTo(-0.12, 0.46);
  s.lineTo(0.12, 0.46);
  s.lineTo(0.35, 0);
  s.closePath();
  const geo = new THREE.ExtrudeGeometry(s, { depth: width, bevelEnabled: false });
  geo.translate(0, 0, -width / 2);
  geo.rotateY(-Math.PI / 2);
  const m = mesh(geo, mat('#8a6a48', { flat: true, rough: 1 }), 0, 0, 0.62);
  return m;
}

function popTarget(kind: TargetKind, x: number, z: number, parent: THREE.Object3D): PopTarget {
  const root = new THREE.Group();
  root.position.set(x, 0, z);
  root.add(mound(kind === 'deer' || kind === 'bigfoot' ? 1.9 : 1.5));
  // A steel stand the plate flips up from.
  root.add(box(0.5, 0.12, 0.25, M.steel, 0, 0.06, 0));
  const plate = new THREE.Group();
  plate.position.y = 0.12;
  let hit: THREE.Mesh;
  if (kind === 'gong') {
    // A round steel plate hanging off a post: it rings.
    plate.add(box(0.06, 0.95, 0.06, M.steel, 0, 0.47, 0));
    const disc = cyl(0.32, 0.32, 0.03, mat('#d9d4c7', { rough: 0.4, metal: 0.6 }), 0, 1.1, 0, 20);
    disc.rotation.x = Math.PI / 2;
    plate.add(disc);
    hit = disc;
  } else {
    const s = SHAPES[kind];
    hit = silhouette(s.points, s.color);
    plate.add(hit);
  }
  root.add(plate);
  parent.add(root);
  const t: PopTarget = { kind, points: SHAPES[kind].score, root, plate, hit, up: 0, want: 0, until: 0 };
  setUp(t, 0);
  return t;
}

/** Plates fall away from the shooter (toward -z), hinged at the bottom. */
export function setUp(t: PopTarget, up: number): void {
  t.up = up;
  t.plate.rotation.x = -(1 - up) * (Math.PI / 2) * 0.97;
  t.root.visible = true;
}

function beerCan(color: string): THREE.Mesh {
  const c = cyl(0.06, 0.06, 0.18, mat(color, { metal: 0.6, rough: 0.3 }), 0, 0, 0, 10);
  c.add(cyl(0.061, 0.061, 0.03, mat('#d9dde0', { metal: 0.7, rough: 0.3 }), 0, 0.075, 0, 10));
  return c;
}

export function buildRange(parent: THREE.Object3D): Range {
  const G = GUN_RANGE;
  const gx = (G.minX + G.maxX) / 2;
  const group = new THREE.Group();
  const targets: PopTarget[] = [
    popTarget('possum', gx - 8, -86, group),
    popTarget('coon', gx - 1.5, -85.5, group),
    popTarget('possum', gx + 6, -86.5, group),
    popTarget('coon', gx - 11, -91, group),
    popTarget('deer', gx - 3, -93, group),
    popTarget('possum', gx + 4, -91.5, group),
    popTarget('deer', gx + 10, -94, group),
    popTarget('gong', gx - 6, -99.5, group),
    popTarget('gong', gx + 2, -100, group),
    popTarget('bigfoot', gx + 7, -101, group),
  ];

  // The running possum on its cable, between the near and middle rows.
  const runZ = -88.5;
  const fromX = G.minX + 1;
  const toX = G.maxX - 1;
  group.add(box(toX - fromX + 2, 0.05, 0.12, M.steel, gx, 0.03, runZ));
  for (const x of [fromX - 0.6, toX + 0.6]) group.add(box(0.3, 0.4, 0.3, M.steel, x, 0.2, runZ));
  const runner = new THREE.Group();
  runner.add(box(0.6, 0.08, 0.3, mat('#5a5f66', { metal: 0.5 }), 0, 0.04, 0));
  const runHit = silhouette(SHAPES.possum.points, '#ffd400');
  runHit.position.y = 0.08;
  runner.add(runHit);
  runner.position.set(fromX, 0, runZ);
  runner.visible = false;
  group.add(runner);

  // Cans on the fence rail (the rail itself is scenery).
  const cans: RangeCan[] = [];
  const colors = ['#c0392b', '#2b6cb0', '#d9d4c7'];
  for (let i = 0; i < 12; i++) {
    const m = beerCan(colors[i % 3] as string);
    const home = new THREE.Vector3(gx - 8.8 + i * 1.6, 1.16, G.minZ + 12);
    m.position.copy(home);
    group.add(m);
    cans.push({ mesh: m, home, hit: false, vel: new THREE.Vector3(), spin: new THREE.Vector3() });
  }

  // The trap house: a plywood box off the east side that throws clays.
  const trap = new THREE.Vector3(G.maxX - 0.5, 0.6, -84);
  const house = new THREE.Group();
  house.add(box(1.4, 1.1, 1.4, mat('#9a7a52'), 0, 0.55, 0));
  house.add(box(1.6, 0.08, 1.6, mat('#5a5652'), 0, 1.14, 0));
  house.add(box(0.7, 0.3, 0.05, M.black, -0.3, 0.75, 0.71));
  house.position.set(trap.x + 0.6, 0, trap.z);
  group.add(house);
  const clay = cyl(0.11, 0.11, 0.04, mat(ORANGE, { rough: 0.6 }), 0, 0, 0, 14);
  clay.visible = false;
  group.add(clay);

  const lanes = [new THREE.Vector3(gx - 2.15, 0, -77.1), new THREE.Vector3(gx + 2.15, 0, -77.1)];
  group.traverse((o) => (o.userData.noBatch = true));
  parent.add(group);

  return {
    targets,
    runner: { root: runner, hit: runHit, fromX, toX, z: runZ },
    cans,
    trap,
    clay,
    lanes,
    reset() {
      for (const t of targets) {
        t.want = 0;
        setUp(t, 0);
      }
      runner.visible = false;
      clay.visible = false;
      for (const c of cans) {
        c.hit = false;
        c.mesh.position.copy(c.home);
        c.mesh.rotation.set(0, 0, 0);
      }
    },
  };
}
